import { useEffect } from "react";
import { useHelpDeskTickets } from "../../context/HelpDeskTicketsContext";
import { CrudTableHeader } from "../../components/crud-table-header";
import { ErrorMessage } from "../../components/error-message";
import CrudTable from "../../components/crud-table";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  HelpDeskTicketStatus,
  HelpDeskTicketStatusBadge,
  HelpDeskTicketStatusTranslation,
  HelpDeskTicketWithDetails,
} from "../../types/help_desk_tickets.types";
import { useTableControls } from "../../hooks/useTableControls";
import { ClipboardIcon } from "@heroicons/react/24/outline";
import { formatPhoneNumber } from "../../utils/format-phone-number.util";
import Label from "../../components/label";
import { Loading } from "../../components/loading";

const columns = [
  {
    key: "id",
    label: "N° Solicitud",
    sortable: true,
    render: (value: number) => value.toString(),
  },
  {
    key: "name",
    label: "Nombre",
    sortable: true,
    render: (value: string) => value,
  },
  {
    key: "email",
    label: "Email",
    sortable: true,
    render: (value: string) => value || "-",
  },
  {
    key: "phone_number",
    label: "Teléfono",
    sortable: true,
    render: (value: string) => formatPhoneNumber(value) || "-",
  },
  {
    key: "title",
    label: "Título",
    sortable: true,
  },
  {
    key: "status",
    label: "Estado",
    sortable: true,
    render: (value: HelpDeskTicketStatus) => (
      <Label
        color={
          HelpDeskTicketStatusBadge[value as HelpDeskTicketStatus].color as any
        }
      >
        {HelpDeskTicketStatusBadge[value].label}
      </Label>
    ),
  },
  {
    key: "responded_by_username",
    label: "Resulto por",
    sortable: true,
    render: (value: string) => value || "N/R",
  },
  {
    key: "created_at",
    label: "Fecha",
    sortable: true,
    render: (value: string) =>
      new Date(value).toLocaleString("es-AR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }),
  },
];

export const HelpDeskCRUD = () => {
  const { helpDeskTickets, isLoading, error, getHelpDeskTickets } =
    useHelpDeskTickets();
  const location = useLocation();
  const isListView = location.pathname === "/dashboard/help-desk";
  const {
    paginatedData: paginatedHelpDeskTickets,
    filteredData,
    totalPages,
    currentPage,
    sortConfig,
    setSortConfig,
    setSearchQuery,
    setSelectedFilters,
    setCurrentPage,
  } = useTableControls(helpDeskTickets || []);
  const navigate = useNavigate();

  useEffect(() => {
    getHelpDeskTickets();
  }, [getHelpDeskTickets]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleSort = (
    sortBy: string | null,
    sortDirection: "asc" | "desc" | null
  ) => {
    setSortConfig({
      sortBy: sortBy as keyof HelpDeskTicketWithDetails | null,
      sortDirection,
    });
  };
  const handleFilterChange = (selectedFilters: Record<string, string[]>) => {
    setSelectedFilters(selectedFilters);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleViewHelpDeskTicket = (
    helpDeskTicket: HelpDeskTicketWithDetails
  ) => {
    navigate(`/dashboard/help-desk/view/${helpDeskTicket.id}`);
  };

  return (
    <div className="w-full p-8">
      {isListView ? (
        <>
          <CrudTableHeader
            title="Solicitudes de ayuda"
            count={helpDeskTickets?.length}
            icon={<ClipboardIcon />}
          />
          {error && (
            <div className="mb-4">
              <ErrorMessage message={error} dismissible />
            </div>
          )}
          {isLoading ? (
            <Loading />
          ) : paginatedHelpDeskTickets  ? (
            <CrudTable
              data={paginatedHelpDeskTickets || []}
              columns={columns}
              actions={{
                view: {
                  label: "",
                  onClick: handleViewHelpDeskTicket,
                },
              }}
              search={{
                placeholder: "Buscar solicitudes...",
                onSearch: handleSearch,
              }}
              sort={{
                sortBy: sortConfig.sortBy,
                sortDirection: sortConfig.sortDirection,
                onSort: handleSort,
              }}
              filters={[
                {
                  key: "status",
                  label: "Estado",
                  multiple: true,
                  options: Object.entries(HelpDeskTicketStatusTranslation).map(
                    ([statusId, statusName]) => ({
                      key: String(statusId),
                      value: statusName,
                    })
                  ),
                },
              ]}
              pagination={{
                page: currentPage,
                totalPages: totalPages,
                onPageChange: handlePageChange,
              }}
              onFilterChange={handleFilterChange}
              totalCount={filteredData.length}
            />
          ) : null}
        </>
      ) : (
        <Outlet />
      )}
    </div>
  );
};
